import { useEffect, useState } from "react";
import { useGetAuftragFilesMutation, useRemoveAuftragFileMutation } from "../../api/AuftragAPI";
import { downloadFile, upload } from "../../services/All";
import { IFilesFromAuftrag } from "../../types/Types";
import { formatToEuroDateTime } from "../../util/DateUtils";
import { LoadingModal } from "../LoadingModal/LoadingModal";
import styles from "./upload.module.scss";
import DialogPdfComponent from "../DialogPdfComponent/DialogPdfComponent";

type UploadFilesParameter = {
  uid: string;
  deleteable?: boolean;
}

const UploadToServer: React.FC<UploadFilesParameter> = ( props: UploadFilesParameter ) => {

  const [dateienVonAuftrag, setDateienVonAuftrag] = useState<IFilesFromAuftrag>();
  const [getAuftragDateien] = useGetAuftragFilesMutation();
  const [removeAuftragFileAPI] = useRemoveAuftragFileMutation();

  const [showImage, setShowImages] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [imageCache, setImageCache] = useState<{ [key: string]: string }>({});
  const [isDragOver, setIsDragOver] = useState(false);

  const [pdfFileUid, setPdfFileUid] = useState<string | undefined>(undefined);
  const [pdfVisible, setPdfVisible] = useState<boolean>(false);

  const handleDownload = async (uid: string) => {
    try {

      downloadFile(uid)
        .then((resp) => {
          const contentDisposition = resp?.headers["content-disposition"] || resp?.headers["Content-Disposition"];
          const headerContentType = resp?.headers["content-type"] || resp?.headers["Content-Type"];
          const contentType = typeof headerContentType === "string"
            ? headerContentType
            : "application/octet-stream";

          let filename = "filename";
          let extractedFileName = filename;
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="(.+)"/);
            if (match && match[1]) {
              extractedFileName = match[1];
            }
          }
          
          const blob = new Blob([resp.data], { type: contentType });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = extractedFileName;
          link.click();
          link.remove();
        });
    } catch (error) {
      console.error("Download-Fehler:", error);
    }
  };

  const getImage = async (uid: string): Promise<string> => {
    try {
      const resp = await downloadFile(uid);
      const headerContentType = resp?.headers["content-type"] || resp?.headers["Content-Type"];
      const contentType = typeof headerContentType === "string"
        ? headerContentType
        : "application/octet-stream";
      const blob = new Blob([resp.data], { type: contentType });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error("Download-Fehler:", error);
      return "";
    }
  };

  const handleRemove = async (fileuid: string) => {
    try {
      removeAuftragFileAPI({ auftragUid: props.uid, fileUid: fileuid }).unwrap()
        .then(() => {
          getAuftragDateien(props.uid).unwrap().then((data) => setDateienVonAuftrag(data));
        });
    } catch (error) {
      console.error("Remove-Fehler:", error);
    }
  };

  const uploadFile = (files: File[]) => {

    setIsLoading(true);

    files.forEach((file, index) => {
      const fileKey = `${file.name}-${index}`;
      upload(props.uid, file, (progress) => {
        setUploadProgress(prev => ({
          ...prev,
          [fileKey]: progress
        }));
      })
        .then(() => {
          getAuftragDateien(props.uid).unwrap().then((data) => setDateienVonAuftrag(data));
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileKey];
            return newProgress;
          });
        })
        .catch(error => {
          console.log(error);
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileKey];
            return newProgress;
          });
        })
        .finally(() => {
          setIsLoading(Object.keys(uploadProgress).length > 1);
        })
        ;
    });
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.dataTransfer.files) {
      uploadFile(Array.from(event.dataTransfer.files));
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    setIsDragOver(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      uploadFile(Array.from(event.target.files));
    }
  };

  useEffect(() => {
    if (!props.uid) return;
    getAuftragDateien(props.uid).unwrap().then((data) => setDateienVonAuftrag(data));
  }, [props.uid]);

  useEffect(() => {
    if (!dateienVonAuftrag?.files) return;

    dateienVonAuftrag.files.forEach(file => {
      if (file.mime.startsWith("image") && !imageCache[file.uid]) {
        getImage(file.uid).then(imageUrl => {
          setImageCache(prev => ({
            ...prev,
            [file.uid]: imageUrl
          }));
        });
      }
    });
  }, [dateienVonAuftrag]);

  return (
    <>

      <LoadingModal show={isLoading} />

      <DialogPdfComponent fileUid={pdfFileUid} deckblattAuftragUid={undefined} dialogVisible={pdfVisible} close={() => { setPdfFileUid(undefined); setPdfVisible(false) }} />

      <div className="p-4 border-2 border-dashed rounded-lg w-96">
        <div
          className={(isDragOver ? styles.dropareaDragOver : "bg-gray-100") + " p-6 text-center rounded-md cursor-pointer "}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <p>Ziehe deine Dateien zum Upload hierher oder</p>
          <label className={(isDragOver ? styles.dropareaDragOver : "text-blue-500") + " underline cursor-pointer"}>
            Durchsuchen
            <input type="file" multiple className="hidden" onChange={handleFileChange} />
          </label>
        </div>
        {Object.entries(uploadProgress).length > 0 && (
          <div className="mt-4">
            {Object.entries(uploadProgress).map(([fileKey, progress]) => (
              <div key={fileKey} className="mb-3">
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">{fileKey.split('-')[0]}</span>
                  <span className="text-sm font-medium">{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <input type="checkbox" onChange={(e) => { setShowImages(showImage ? false : true) }} /> Alle Bilder anzeigen

      {dateienVonAuftrag?.files.map((e, index) =>
        <div key={index} className={styles["auftragCard"]} >

          <div className={styles.fileCard + " card"}>
            <div className={styles.fileInfo}>
              <div className={styles.fileIcon}>

                {(e.mime === "application/pdf") ?
                  <>
                    <button className="btn btn-secondary" onClick={() => { setPdfFileUid(e.uid); setPdfVisible(true); }}>
                      Vorschau
                    </button>
                  </>
                  :
                  (e.mime.startsWith("image") && showImage) ?
                    <>
                      {imageCache[e.uid] && <img src={imageCache[e.uid]} width={100} />}
                    </>
                    :
                    <>
                      <i className="bi bi-file-earmark-text"></i>
                    </>
                }
              </div>

              <div className={styles.fileText}>
                <div className={styles.fileName}>{e.filename}</div>
                <div className={styles.fileMeta}>
                  📅 Hochgeladen am {formatToEuroDateTime(e.createDateTime)}
                </div>
              </div>
              <div className={styles.fileActions}>
                <button className="btn btn-primary btn-sm" onClick={() => { handleDownload(e.uid) }}><i className="bi bi-cloud-arrow-down-fill" />&nbsp;Download</button>
                {props.deleteable !== false && (
                  <button className="btn btn-secondary btn-sm" onClick={() => { handleRemove(e.uid) }}><i className="bi bi-trash" /></button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/*<div className="card">
        <FileUpload name="demoFileUpload"
          url={process.env.REACT_APP_API_URL! + process.env.REACT_APP_API_VERSION! + 'imageUpload'}
          multiple
          accept="image/*"
          maxFileSize={5000000}
          emptyTemplate={<p className="m-0">Drag and drop files to here to upload.</p>}
          customUpload
          auto={true}
          uploadHandler={async (event) => {
            const filelist = event.files;
            if (filelist !== undefined && filelist !== null) {
              uploadFile(filelist[0]);
            }
            event.options.clear();
          }}
        />
      </div>*/}
    </>
  )
}

export default UploadToServer;